import type { BadgeDescription } from 'features/catalog/types';

/** Tenure ladder for historical offsite attendance (years of tenure → badge id). */
export const OFFSITE_TENURE_LADDER = [
  { years: 1, id: 'tpbo' },
  { years: 2, id: '86xv' },
  { years: 4, id: 'gu8b' },
  { years: 6, id: 'nogh' },
  { years: 8, id: 'eoak' },
] as const;

/** CoinMetrics → Talos Golden Handoff event date (local calendar). */
export const GOLDEN_HANDOFF_DATE = new Date(2025, 6, 30);

/** First Talos intern start date — Unlocked: Full-Time is impossible before this. */
export const FIRST_INTERN_HIRE_DATE = new Date(2024, 5, 1);

/** Anton last played Among Us — Anton's Impostor years-since tenure gate anchor. */
export const ANTON_LAST_AMONG_US_DATE = new Date(2021, 6, 30);

/** End of 2025 NYC Coat Drive window — Warmth Warrior years-since tenure gate anchor. */
export const NYC_COAT_DRIVE_2025_DATE = new Date(2026, 0, 1);

/** 2023 EMEA / NA offsite pair — owning one soft-hides the other (still claimable). */
export const OFFSITE_2023_BADGE_IDS = ['wf23', 'wl23'] as const;

/**
 * Owning an N-year tenure badge proves start ≤ now − N years. If that latest possible start
 * is still before `eventDate` (i.e. now < eventDate + N years), the person was guaranteed
 * employed before the event — use to ban event-only badges (Golden Handoff, Unlocked: Full-Time).
 */
export const isTenureProofBeforeDate = (
  eventDate: Date,
  ownedBadgeIds: ReadonlySet<string> | ReadonlyMap<string, boolean>,
  now: Date = new Date()
): boolean => {
  const owns = (id: string) => (ownedBadgeIds instanceof Map ? !!ownedBadgeIds.get(id) : ownedBadgeIds.has(id));

  for (const { years, id } of OFFSITE_TENURE_LADDER) {
    if (!owns(id)) continue;
    const banUntil = new Date(eventDate);
    banUntil.setFullYear(banUntil.getFullYear() + years);
    if (now.getTime() < banUntil.getTime()) return true;
  }
  return false;
};

/** Golden Handoff (`coi1`) — banned when tenure proves employment before 2025-07-30. */
export const isGoldenHandoffBannedByTenure = (
  ownedBadgeIds: ReadonlySet<string> | ReadonlyMap<string, boolean>,
  now: Date = new Date()
): boolean => isTenureProofBeforeDate(GOLDEN_HANDOFF_DATE, ownedBadgeIds, now);

/** Unlocked: Full-Time (`intr`) — banned when tenure proves employment before the first intern (2024-06-01). */
export const isUnlockedFullTimeBannedByTenure = (
  ownedBadgeIds: ReadonlySet<string> | ReadonlyMap<string, boolean>,
  now: Date = new Date()
): boolean => isTenureProofBeforeDate(FIRST_INTERN_HIRE_DATE, ownedBadgeIds, now);

/**
 * Tenure required by how long ago an event was — same ladder as offsites.
 * Only on/after the N-year calendar anniversary of `eventDate` does the N-year
 * badge become the gate (e.g. 6-year required from 2027-07-30 for Anton).
 * No gate until the 1-year anniversary.
 */
export const tenureGateForYearsSinceEvent = (
  eventDate: Date,
  now = new Date()
): { count: number; ids: string[] } | undefined => {
  const eventMs = eventDate.getTime();
  if (Number.isNaN(eventMs) || now.getTime() < eventMs) return undefined;

  let yearsSince = now.getFullYear() - eventDate.getFullYear();
  const anniversaryThisYear = new Date(eventDate);
  anniversaryThisYear.setFullYear(now.getFullYear());
  if (now.getTime() < anniversaryThisYear.getTime()) yearsSince -= 1;

  let tenureId: string | undefined;
  for (const step of OFFSITE_TENURE_LADDER) {
    if (yearsSince >= step.years) tenureId = step.id;
  }
  return tenureId ? { count: 1, ids: [tenureId] } : undefined;
};

/** True when the years-since ladder requires a tenure rung the profile does not own. */
export const isBannedByYearsSinceTenureGate = (
  eventDate: Date,
  ownedBadgeIds: ReadonlySet<string> | ReadonlyMap<string, boolean>,
  now: Date = new Date()
): boolean => {
  const gate = tenureGateForYearsSinceEvent(eventDate, now);
  if (!gate) return false;
  const owns = (id: string) => (ownedBadgeIds instanceof Map ? !!ownedBadgeIds.get(id) : ownedBadgeIds.has(id));
  const requiredYears = OFFSITE_TENURE_LADDER.find((step) => step.id === gate.ids[0])?.years;
  if (requiredYears == null) return true;
  // Accept the required rung or any higher (higher auto-claims lower in the live map).
  for (const step of OFFSITE_TENURE_LADDER) {
    if (step.years >= requiredYears && owns(step.id)) return false;
  }
  return true;
};

/**
 * Anton's Impostor (`xray`) — same years-since gate as offsites, anchored to
 * Anton last played (2021-07-30). Mid-2026 still only needs Emerald; Wisdom Guardian
 * only after 2027-07-30.
 */
export const isAntonImpostorBannedByTenure = (
  ownedBadgeIds: ReadonlySet<string> | ReadonlyMap<string, boolean>,
  now: Date = new Date()
): boolean => isBannedByYearsSinceTenureGate(ANTON_LAST_AMONG_US_DATE, ownedBadgeIds, now);

/**
 * Warmth Warrior (`warm`) — NYC office (`ziyg`) stays on `restrictedRules`; tenure uses the
 * same years-since ladder anchored to 2026-01-01 (no tenure gate until 2027-01-01).
 */
export const isWarmthWarriorBannedByTenure = (
  ownedBadgeIds: ReadonlySet<string> | ReadonlyMap<string, boolean>,
  now: Date = new Date()
): boolean => isBannedByYearsSinceTenureGate(NYC_COAT_DRIVE_2025_DATE, ownedBadgeIds, now);

/**
 * Soft-hide the other 2023 offsite when one is already on the profile. Still claimable
 * (not locked) — only collapsed into the “hidden” tile. Callers should pass the edit
 * baseline (profile IDs), not the live session map, so picking one mid-edit doesn’t jump.
 */
export const isSoftHiddenBySiblingOffsite = (
  badgeId: string,
  ownedBadgeIds: ReadonlySet<string> | ReadonlyMap<string, boolean>
): boolean => {
  const owns = (id: string) => (ownedBadgeIds instanceof Map ? !!ownedBadgeIds.get(id) : ownedBadgeIds.has(id));
  if (badgeId === 'wf23') return owns('wl23');
  if (badgeId === 'wl23') return owns('wf23');
  return false;
};

/**
 * Offsites land in autumn. Use Nov 1 as the event anchor so October attendees still
 * clear the next tenure rung before the gate steps up (e.g. Nice 2022 stays on Emerald
 * through Oct 2026; Wisdom Guardian from Nov 1 2026). Scales as time passes
 * (e.g. ~8 years since → Oracle / 8+). No tenure gate until 1 year after the anchor.
 */
export const restrictedRulesForOffsiteYear = (
  eventYear: number,
  now = new Date()
): { count: number; ids: string[] } | undefined => tenureGateForYearsSinceEvent(new Date(eventYear, 10, 1), now);

/** Prefer dynamic tenure gates when `offsiteYear` or `tenureGateEventDate` is set. */
export const resolveRestrictedRules = (
  desc: Pick<BadgeDescription, 'restrictedRules' | 'offsiteYear' | 'tenureGateEventDate'>,
  now = new Date()
): { count: number; ids: string[] } | undefined => {
  if (desc.offsiteYear != null) return restrictedRulesForOffsiteYear(desc.offsiteYear, now);
  if (desc.tenureGateEventDate) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(desc.tenureGateEventDate);
    if (match) {
      return tenureGateForYearsSinceEvent(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])), now);
    }
  }
  return desc.restrictedRules;
};
